(() => {
  const elementName = 'inventa-recently-viewed';
  const storageKey = 'theme:recently-viewed-products';
  if (customElements.get(elementName)) return;

  const productId = value => {
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    if (!/^\d+$/.test(String(value))) return null;
    const id = Number(value);
    return Number.isSafeInteger(id) && id > 0 ? String(id) : null;
  };

  class InventaRecentlyViewed extends HTMLElement {
    constructor() {
      super();
      this.sequence = 0;
      this.onPageShow = event => { if (event.persisted) this.refresh(true); };
      this.onPageHide = () => this.cancel();
      this.onStorage = event => { if (event.key === storageKey || event.key === null) this.refresh(); };
    }

    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('storage', this.onStorage);
      queueMicrotask(() => { if (this.connected && this.isConnected) this.refresh(); });
    }

    disconnectedCallback() {
      this.connected = false;
      this.cancel();
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('storage', this.onStorage);
    }

    cancel() {
      this.sequence += 1;
      this.controller?.abort();
      this.controller = null;
      this.requestKey = null;
      this.removeAttribute('aria-busy');
    }

    hide(state) {
      this.hidden = true;
      const wrapper = this.closest('.shopify-section');
      if (wrapper) wrapper.hidden = true;
      this.dataset.state = state;
      this.querySelector('[data-recent-list]')?.replaceChildren();
    }

    readIds() {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(stored)) return [];
      const exclude = productId(this.dataset.excludeId);
      const limit = Math.min(12, Math.max(2, Number(this.dataset.limit) || 9));
      return [...new Set(stored.map(productId).filter(Boolean))].slice(0, 24).filter(id => id !== exclude).slice(0, limit);
    }

    async refresh(force = false) {
      if (!this.connected || !this.isConnected) return;
      let ids;
      try { ids = this.readIds(); }
      catch { this.cancel(); this.hide('empty'); return; }
      if (!ids.length) { this.cancel(); this.hide('empty'); return; }

      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(`${root}search`, location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', ids.map(id => `id:${id}`).join(' OR '));
      url.searchParams.set('section_id', this.dataset.sectionId);
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const requestKey = url.href;
      if (!force && this.requestKey === requestKey) return;
      this.cancel();
      const sequence = this.sequence;
      const controller = new AbortController();
      this.controller = controller;
      this.requestKey = requestKey;
      this.hide('loading');
      this.setAttribute('aria-busy', 'true');
      const current = () => sequence === this.sequence && this.connected && this.isConnected && !controller.signal.aborted;

      try {
        const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Recently viewed section request failed');
        const html = await response.text();
        if (!current()) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = [...parsed.querySelectorAll('inventa-recently-viewed')].find(host => host.dataset.sectionId === this.dataset.sectionId);
        const sourceList = source?.querySelector('[data-recent-list]');
        if (!sourceList) throw new Error('Recently viewed section is missing');
        const byId = new Map();
        for (const card of sourceList.querySelectorAll(':scope > [data-inventa-product-item][data-recent-product-id]')) {
          const id = productId(card.dataset.recentProductId);
          if (id && ids.includes(id) && !byId.has(id) && card.querySelector('[data-inventa-product-card]')) byId.set(id, card);
        }
        const cards = ids.filter(id => byId.has(id)).map(id => document.importNode(byId.get(id), true));
        const list = this.querySelector('[data-recent-list]');
        if (!cards.length || !list) { this.hide('empty'); return; }
        list.replaceChildren(...cards);
        this.dataset.state = 'success';
        this.hidden = false;
        const wrapper = this.closest('.shopify-section');
        if (wrapper) wrapper.hidden = false;
        document.dispatchEvent(new Event('inventa:wishlist-buttons-added'));
      } catch {
        if (current()) { this.requestKey = null; this.hide('error'); }
      } finally {
        if (current()) {
          this.controller = null;
          this.removeAttribute('aria-busy');
        }
      }
    }
  }

  customElements.define(elementName, InventaRecentlyViewed);
})();
